import {
  Box,
  Paper,
  Tab,
  Tabs,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import React from "react";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import TabPanelFormat from "../../../../components/widgets/TabPanelFormat";
import { getHashdata } from "./utils";
import { setHash } from "../../../../components/Tabs/Utils/hashFunctions";
import { pvTabs } from "./PVTabs";
import { PVContext } from "./PV.Context";
import LoadProjectDataComboBox from "./LoadProjectDataComboBox";
import { getPlantById, getAllData } from "./api";
import useEmsTreeAvailable from "../../../../hooks/useEmsTreeAvailable";
import { useGeneralContext } from "../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";
import { usePVContext } from "./PressureVesselData/PVContext";
import EdgeMentorHost from "../../AllEquipments/CommonFeatures/Edge Mentor/EdgeMentorHost";
import { resolvePVEdgeMentorCodeLabel } from "./edgeMentorResolver";

const NEW_REVISION_OPTION = "__new";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Mirrors PVContext.jsx's revision storage (flat inputData[rev] map) so the
// Rev. dropdown reflects the revisions actually saved for this project in
// the backend, rather than the FreeCAD-only revision list the old
// ModelingControls tool used.
function extractRevisionKeys(allData) {
  const bucket = isPlainObject(allData?.inputData) ? allData.inputData : {};

  const keys = Object.keys(bucket).filter((k) => !isNaN(Number(k)));
  if (!keys.includes("0")) keys.push("0");

  return keys.sort((a, b) => Number(a) - Number(b));
}

const compactComboSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 18,
    height: 22,
    padding: "0 28px 0 8px !important",
    fontSize: 11.5,
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "0 !important",
    height: 22,
    lineHeight: "22px",
    fontSize: 11.5,
  },
  "& .MuiInputLabel-root": {
    fontSize: 11.5,
    transform: "translate(14px, 4px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

function HeaderField({ label, children, minWidth = 160 }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function ProjectSelectionHeader({
  plantId,
  clientName,
  type,
  selectedProject,
  onProjectSelect,
}) {
  const [plantName, setPlantName] = React.useState("");
  const [revisions, setRevisions] = React.useState(["0"]);

  // With the EMS tree on screen it is the single place a WBS/revision gets
  // picked, so the header only reports the selection - on every tab,
  // Design included.
  const treeOwnsSelection = useEmsTreeAvailable();

  const {
    selectedModelingRevision,
    setSelectedModelingRevision,
    setRevision,
    isDesignTabActive,
  } = useGeneralContext();

  const canEditSelection = isDesignTabActive && !treeOwnsSelection;
  const { confirmedRevision, setConfirmedRevision, savePVData } =
    usePVContext();

  // The dropdown only ever shows a revision as "selected" once it has
  // actually been confirmed (and thus loaded) for the currently selected
  // project - never a leftover value from a previously viewed project.
  const displayedRevision =
    confirmedRevision && confirmedRevision.projectId === selectedProject?.id
      ? confirmedRevision.revision
      : null;

  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleSave = async () => {
    setSaving(true);

    try {
      const result = await savePVData();
      setSnackbar({
        open: true,
        severity: result?.ok ? "success" : "warning",
        message: result?.ok
          ? `Saved revision ${selectedModelingRevision}`
          : "Save completed with some failures",
      });
    } catch (error) {
      console.error("Failed to save PV data", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.message || "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (!plantId) return;

    let cancelled = false;

    getPlantById(plantId)
      .then((plant) => {
        if (!cancelled) setPlantName(plant?.name || "");
      })
      .catch((error) => {
        console.log("Failed to load plant", error);
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  React.useEffect(() => {
    if (!selectedProject?.id) {
      setRevisions(["0"]);
      return;
    }

    let cancelled = false;

    getAllData(selectedProject.id)
      .then((res) => {
        if (cancelled) return;

        const keys = extractRevisionKeys(res?.all);
        setRevisions(keys);

        // Only populate the options here - loading is deferred until the
        // user explicitly picks a revision (see handleRevisionChange), so
        // selecting a WBS project alone never triggers a data load.
      })
      .catch((error) => {
        console.log("Failed to load revisions", error);
        if (!cancelled) setRevisions(["0"]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id]);

  const handleRevisionChange = (_event, value) => {
    if (!value || !selectedProject?.id) return;

    if (value === NEW_REVISION_OPTION) {
      const nextRev = String(Math.max(0, ...revisions.map(Number)) + 1);
      setRevisions((prev) =>
        [...prev, nextRev].sort((a, b) => Number(a) - Number(b)),
      );
      setSelectedModelingRevision(nextRev);
      setRevision(nextRev);
      setConfirmedRevision({
        projectId: selectedProject.id,
        revision: nextRev,
      });
      return;
    }

    setSelectedModelingRevision(value);
    setRevision(value);
    setConfirmedRevision({ projectId: selectedProject.id, revision: value });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        px: 1.5,
        py: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        {/* keep this in the left */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderOpenRoundedIcon fontSize="small" />
          </Stack>

          <HeaderField label="Project UID">
            <Typography variant="body2" fontWeight={600} noWrap>
              {plantName || "—"}
            </Typography>
          </HeaderField>

          <HeaderField label="WBS UID" minWidth={260}>
            {canEditSelection ? (
              <LoadProjectDataComboBox
                plantId={plantId}
                clientName={clientName}
                type={type}
                onSelect={onProjectSelect}
              />
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {selectedProject?.name || selectedProject?.link || "—"}
              </Typography>
            )}
          </HeaderField>

          <HeaderField label="Rev." minWidth={110}>
            {canEditSelection ? (
              <Autocomplete
                size="small"
                fullWidth
                disabled={!selectedProject}
                options={[...revisions, NEW_REVISION_OPTION]}
                value={displayedRevision}
                getOptionLabel={(option) =>
                  option == null
                    ? ""
                    : option === NEW_REVISION_OPTION
                      ? "+ Add New Revision"
                      : String(option)
                }
                onChange={handleRevisionChange}
                sx={compactComboSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select revision" />
                )}
              />
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {selectedModelingRevision || "0"}
              </Typography>
            )}
          </HeaderField>

          <Button
            className="header-save-btn"
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{ alignSelf: "center", flexShrink: 0 }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default function PVMain() {
  const { project, currentTab, setCurrentTab } = React.useContext(PVContext);

  const [selectedProject, setSelectedProject] = React.useState(null);

  const { plantId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const { setIsDesignTabActive, setSelectedModelingRevision, setRevision } =
    useGeneralContext();
  const { setSelectedProjectId, setConfirmedRevision, confirmedRevision } =
    usePVContext();

  const tabs = pvTabs(project);

  // Nothing renders until a revision is confirmed in the header — the picker
  // itself lives above the tabs, so it stays reachable.
  // Same check the Rev. box uses: a confirmation from a previously selected
  // project must not keep the gate open on the new one.
  const hasRevision = Boolean(
    confirmedRevision && confirmedRevision.projectId === selectedProject?.id,
  );

  const gate = (panel) =>
    hasRevision ? (
      panel
    ) : (
      <Box sx={{ p: 4, width: "100%", textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Select a WBS UID and a revision above to load this project.
        </Typography>
      </Box>
    );

  // Deep link from the DCS sidebar ("Input Data" nodes under a PV project):
  // /plant/:plantId/estimation?projectId=&revision=&projectName= opens
  // straight into that project's saved Input Data revision, the same way
  // picking it manually via the WBS/Rev. dropdowns would.
  // Keyed on the whole link, not just "have we deep-linked once": picking
  // another revision in the tree while this page is already open has to switch
  // the data and the shown revision straight away.
  const appliedDeepLinkRef = React.useRef(null);

  React.useEffect(() => {
    const deepLinkProjectId = searchParams.get("projectId");
    const deepLinkRevision = searchParams.get("revision");
    if (!deepLinkProjectId || deepLinkRevision == null) return;

    const key = `${deepLinkProjectId}|${deepLinkRevision}`;
    if (appliedDeepLinkRef.current === key) return;

    appliedDeepLinkRef.current = key;

    setSelectedProject({
      id: deepLinkProjectId,
      name: searchParams.get("projectName") || "",
    });
    setSelectedProjectId(deepLinkProjectId);
    setSelectedModelingRevision(deepLinkRevision);
    setRevision(deepLinkRevision);
    setConfirmedRevision({
      projectId: deepLinkProjectId,
      revision: deepLinkRevision,
    });
  }, [
    searchParams,
    setConfirmedRevision,
    setRevision,
    setSelectedModelingRevision,
    setSelectedProjectId,
  ]);

  const navLabel = "pv-modeling";

  const clientName = "DEMO";
  const type = "pv";

  // WBS UID / Rev. in the header are only editable on the Design sub-tab,
  // which only exists inside "Input Data" - leaving that top-level tab
  // (Estimation, Calculation) always makes them read-only.
  React.useEffect(() => {
    if (tabs[currentTab]?.label !== "Input Data") {
      setIsDesignTabActive(false);
    }
  }, [currentTab, tabs, setIsDesignTabActive]);

  React.useEffect(() => {
    if (!tabs.length) return;

    const fullHash = window.location.hash;
    const hashValue = getHashdata(fullHash, navLabel);

    const tabIndex = tabs.findIndex(
      (tab) => tab.label === decodeURIComponent(hashValue),
    );

    if (tabIndex !== -1) {
      setCurrentTab(tabIndex);
    } else if (fullHash) {
      window.location.hash =
        fullHash + "&" + navLabel + "=" + encodeURIComponent(tabs[0].label);
    } else {
      window.location.hash = navLabel + "=" + encodeURIComponent(tabs[0].label);
    }
  }, []);

  // Mount alone isn't enough: coming from the EMS tree straight to an
  // Estimation node while this page is already open changes the hash without
  // remounting, and the sub-tab has to follow. Reads window.location.hash
  // rather than location.hash because handleChange writes it outside the
  // router.
  React.useEffect(() => {
    if (!tabs.length) return;

    const hashValue = getHashdata(window.location.hash, navLabel);
    if (hashValue == null) return;

    const tabIndex = tabs.findIndex(
      (tab) => tab.label === decodeURIComponent(hashValue),
    );
    if (tabIndex !== -1) setCurrentTab(tabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue);
    setHash(window.location.hash, navLabel, tabs[newValue].label);
  };

  const handleProjectSelect = (selectedProjectData) => {
    console.log("selected PV project", selectedProjectData);
    setSelectedProject(selectedProjectData);

    // If you have a setter inside PVContext, call it here.
    // Example:
    // setProject(selectedProjectData);
    // setModelingData(selectedProjectData?.data);
  };

  return (
    <EdgeMentorHost resolveCodeLabel={resolvePVEdgeMentorCodeLabel}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          p: 0,
        }}
      >
        <ProjectSelectionHeader
          plantId={plantId}
          clientName={clientName}
          type={type}
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
        />

        <Paper
          sx={{
            width: "100%",
            display: "flex",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", overflow: "auto" }}>
            <Tabs
              value={currentTab}
              onChange={handleChange}
              orientation="horizontal"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, i) => (
                <Tab key={tab.label} label={tab.label} value={i} />
              ))}
            </Tabs>

            {tabs[currentTab] && (
              <TabPanelFormat padding={0} {...tabs[currentTab].panelProps}>
                {gate(tabs[currentTab].panel)}
              </TabPanelFormat>
            )}
          </Box>
        </Paper>
      </Box>
    </EdgeMentorHost>
  );
}
